import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {Strand} from 'constants/MolbioConstants';
import BulkEditModal from 'molbio/sequence-features/BulkEditModal';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import StrandDropdown from 'molbio/sequence-features/StrandDropdown';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import AnnotationTypeDropdownInput from './AnnotationTypeDropdownInput';
import {AnnotationData} from './AnnotationUtil';

interface FormValueProps<T> {
  value: T;
  setValue: (value: T) => void;
  allSameValue: boolean;
}

interface AnnotationBulkEditModalContentProps {
  isSingleStranded: boolean;
  allAnnotationTypes: Array<string>;
  typeProps: FormValueProps<string | null>;
  strandProps: FormValueProps<Strand | null>;
  colorProps: FormValueProps<string | null>;
}

function AnnotationBulkEditModalContent({
  isSingleStranded,
  allAnnotationTypes,
  typeProps,
  strandProps,
  colorProps,
}: AnnotationBulkEditModalContentProps): JSX.Element {
  return (
    <div className={css(styles.formContainer)}>
      <FormGroup className={css(styles.inputColumn)}>
        <FieldLabel className={css(styles.label)} label="Annotation type" />
        <AnnotationTypeDropdownInput
          type={typeProps.value}
          allTypes={allAnnotationTypes}
          onChangeType={typeProps.setValue}
          includeNullOption={!typeProps.allSameValue}
        />
      </FormGroup>

      <FormGroup className={css(styles.inputColumn)}>
        <FieldLabel className={css(styles.label)} label="Strand" />
        <StrandDropdown
          strand={strandProps.value}
          onSelectStrand={strandProps.setValue}
          includeNullOption={!strandProps.allSameValue}
          disabled={isSingleStranded}
        />
      </FormGroup>

      <FormGroup className={css(styles.inputColumn)}>
        <FieldLabel className={css(styles.label)} label="Color" />
        <PopoverColorPicker
          selectedColor={colorProps.value}
          onSelectColor={colorProps.setValue}
          asDropdown={true}
        />
      </FormGroup>
    </div>
  );
}

export interface BulkEditAnnotationProperties {
  strand?: Strand;
  color?: string;
  annotation_type?: string;
}

export interface AnnotationBulkEditModalProps {
  selectedAnnotations: Array<AnnotationData>;
  isSingleStranded: boolean;
  allAnnotationTypes: Array<string>;
  bulkEditAnnotations: (updatedProperties: BulkEditAnnotationProperties) => void;
  onRequestClose: () => void;
}

/**
 * Modal to bulk edit annotations
 */
export default function AnnotationBulkEditModal({
  selectedAnnotations,
  allAnnotationTypes,
  isSingleStranded,
  bulkEditAnnotations,
  onRequestClose,
}: AnnotationBulkEditModalProps): JSX.Element {
  // This modal cannot be opened if there are no selected annotations
  const firstAnnotation = selectedAnnotations[0];
  const allSameStrand = getAllSameAttribute('strand');
  const allSameType = getAllSameAttribute('annotation_type');
  const allSameColor = getAllSameAttribute('color');
  const [strand, setStrand] = useState(allSameStrand ? firstAnnotation.strand : null);
  const [type, setType] = useState(
    getAllSameAttribute('annotation_type') ? firstAnnotation.annotation_type : null
  );
  const [color, setColor] = useState(getAllSameAttribute('color') ? firstAnnotation.color : null);

  function getAllSameAttribute(key: keyof AnnotationData): boolean {
    return selectedAnnotations.every((annotation) => annotation[key] === firstAnnotation[key]);
  }

  function onClickSave(): void {
    const strandChanged = strand != null && !(allSameStrand && strand === firstAnnotation.strand);
    const typeChanged = type != null && !(allSameType && type === firstAnnotation.annotation_type);
    const colorChanged = color != null && !(allSameColor && color === firstAnnotation.color);
    if (strandChanged || typeChanged || colorChanged) {
      // If any values need to be changed, update only the changed properties
      const updatedProperties = {
        ...(strandChanged && {strand}),
        ...(typeChanged && {annotation_type: type}),
        ...(colorChanged && {color}),
      };
      bulkEditAnnotations(updatedProperties);
    }
    onRequestClose();
  }

  return (
    <BulkEditModal
      onRequestClose={onRequestClose}
      collectionName="annotation"
      featureNames={selectedAnnotations.map(({name}) => name)}
      onClickSave={onClickSave}
    >
      <AnnotationBulkEditModalContent
        isSingleStranded={isSingleStranded}
        allAnnotationTypes={allAnnotationTypes}
        strandProps={{value: strand, setValue: setStrand, allSameValue: allSameStrand}}
        typeProps={{value: type, setValue: setType, allSameValue: allSameType}}
        colorProps={{value: color, setValue: setColor, allSameValue: allSameColor}}
      />
    </BulkEditModal>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    display: 'flex',
    gap: '16px',
  },
  label: {
    display: 'block',
  },
  inputColumn: {
    flex: 1,
  },
});
